import { useMemo, useState, type ReactElement } from 'react';
import { KeyRound, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/shell/PageHeader';

import {
  useLocalOidcAvailability,
  useOidcCredentialMutations,
  useOidcCredentials,
  type OidcCredentialInput,
  type OidcCredentialViewModel,
} from '@/features/admin-auth/api';
import { DeleteAuthConfirmDialog } from '@/features/admin-auth/components/DeleteAuthConfirmDialog';
import { OidcCredentialFormDialog } from '@/features/admin-auth/components/OidcCredentialFormDialog';
import { OidcCredentialsTable } from '@/features/admin-auth/components/OidcCredentialsTable';

export function AdminOidcCredentialsPage(): ReactElement {
  const { t } = useTranslation();
  const query = useOidcCredentials();
  const { create, createLocal, update, remove } = useOidcCredentialMutations();
  const { data: localAvailable = false } = useLocalOidcAvailability();

  const [formOpen, setFormOpen] = useState(false);
  const [formInitial, setFormInitial] = useState<OidcCredentialViewModel | null>(null);
  // Which create flow the open form is running (null while editing). Local mode hides the credential
  // fields and posts to the backend one-click endpoint.
  const [createMode, setCreateMode] = useState<'local' | 'external' | null>(null);
  const [deleteRow, setDeleteRow] = useState<OidcCredentialViewModel | null>(null);

  const rows = useMemo(
    () => (query.isSuccess ? [...query.data].sort((a, b) => a.label.localeCompare(b.label)) : []),
    [query.isSuccess, query.data],
  );

  const isEdit = formInitial !== null;
  const isLocalCreate = !isEdit && createMode === 'local';
  const formPending = isEdit
    ? update.isPending
    : isLocalCreate
      ? createLocal.isPending
      : create.isPending;
  const formError = isEdit ? update.error : isLocalCreate ? createLocal.error : create.error;

  function resetMutations(): void {
    create.reset();
    createLocal.reset();
    update.reset();
  }

  function closeForm(): void {
    setFormOpen(false);
    setFormInitial(null);
    setCreateMode(null);
    resetMutations();
  }

  function openCreate(mode: 'local' | 'external'): void {
    resetMutations();
    setFormInitial(null);
    setCreateMode(mode);
    setFormOpen(true);
  }

  function handleFormSubmit(input: OidcCredentialInput): void {
    if (formInitial !== null) {
      update.mutate({ ...input, id: formInitial.id }, { onSuccess: closeForm });
    } else if (createMode === 'local') {
      createLocal.mutate(
        { label: input.label, type: input.type, isDefault: input.isDefault, scopes: input.scopes },
        { onSuccess: closeForm },
      );
    } else {
      create.mutate(input, { onSuccess: closeForm });
    }
  }

  return (
    <>
      <PageHeader
        icon={<KeyRound className="h-[18px] w-[18px]" aria-hidden="true" />}
        title={t('routes.admin.oidcCredentials.heading')}
        subtitle={t('routes.admin.oidcCredentials.subheading')}
      />
      <div className="px-8 py-5 space-y-8 bg-background">
        <section aria-labelledby="admin-oidc-heading" className="space-y-4">
          <header className="flex items-start justify-between gap-4">
            <div>
              <h2 id="admin-oidc-heading" className="text-base font-semibold text-foreground">
                {t('routes.admin.oidcCredentials.listHeading')}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('routes.admin.oidcCredentials.listSubheading')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {localAvailable && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    openCreate('local');
                  }}
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  {t('routes.admin.oidcCredentials.addLocalAction')}
                </Button>
              )}
              <Button
                size="sm"
                onClick={() => {
                  openCreate('external');
                }}
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                {t('routes.admin.oidcCredentials.addExternalAction')}
              </Button>
            </div>
          </header>

          {query.isPending && <RowsSkeleton />}

          {query.isError && (
            <div className="max-w-xl">
              <FetchErrorCallout
                error={query.error}
                heading={t('routes.admin.oidcCredentials.error.heading')}
                message={t('routes.admin.oidcCredentials.error.message')}
                codeLabel={t('routes.admin.oidcCredentials.error.codeLabel')}
                onRetry={() => {
                  query.refetch().catch(() => undefined);
                }}
                retryLabel={t('routes.admin.oidcCredentials.error.retry')}
              />
            </div>
          )}

          {query.isSuccess && rows.length === 0 && (
            <p className="text-sm text-muted-foreground" role="status">
              {t('routes.admin.oidcCredentials.empty')}
            </p>
          )}

          {query.isSuccess && rows.length > 0 && (
            <DataTable aria-label={t('routes.admin.oidcCredentials.heading')}>
              <OidcCredentialsTable
                rows={rows}
                onEdit={(row) => {
                  resetMutations();
                  setCreateMode(null);
                  setFormInitial(row);
                  setFormOpen(true);
                }}
                onDelete={(row) => {
                  remove.reset();
                  setDeleteRow(row);
                }}
                pendingDeleteId={remove.isPending && deleteRow !== null ? deleteRow.id : null}
              />
            </DataTable>
          )}
        </section>
      </div>

      <OidcCredentialFormDialog
        open={formOpen}
        initial={formInitial}
        local={isLocalCreate}
        onOpenChange={(next) => {
          if (!next && formPending) return;
          if (!next) closeForm();
          else setFormOpen(true);
        }}
        onSubmit={handleFormSubmit}
        pending={formPending}
        submitError={formError}
      />

      <DeleteAuthConfirmDialog
        open={deleteRow !== null}
        title={t('routes.admin.oidcCredentials.delete.title')}
        message={t('routes.admin.oidcCredentials.delete.message', { label: deleteRow?.label ?? '' })}
        cancelLabel={t('routes.admin.oidcCredentials.delete.cancel')}
        confirmLabel={t('routes.admin.oidcCredentials.delete.confirm')}
        onCancel={() => {
          if (remove.isPending) return;
          setDeleteRow(null);
          remove.reset();
        }}
        onConfirm={() => {
          if (deleteRow === null) return;
          remove.mutate(deleteRow.id, {
            onSuccess: () => {
              setDeleteRow(null);
              remove.reset();
            },
          });
        }}
        pending={remove.isPending}
        submitError={remove.error}
        submitErrorLabel={(code) => t('routes.admin.oidcCredentials.form.errors.submit', { code })}
      />
    </>
  );
}

function RowsSkeleton(): ReactElement {
  return (
    <div className="space-y-2" aria-hidden="true">
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full rounded-md" />
      ))}
    </div>
  );
}
